<template>
  <div class="workflow-status-table" data-testid="workflow-status-table">
    <!-- Profit & Loss Table -->
    <div class="text-subtitle-2 mb-2 pl-1 font-weight-bold grey--text text--darken-2">Profit & Loss</div>
    <v-simple-table dense class="workflow-table mb-4" data-testid="workflow-status-table__pl-table">
      <template v-slot:default>
        <thead class="tableHeaderBg">
          <tr data-testid="workflow-status-table__pl-header-row">
            <th class="text-left" data-testid="workflow-status-table__pl-header-workflow-type" style="width: 40%;">
              Workflow Type
            </th>
            <th class="text-left" data-testid="workflow-status-table__pl-header-proforma" style="width: 30%;">
              Proforma
            </th>
            <th class="text-left" data-testid="workflow-status-table__pl-header-forecast" style="width: 30%;">
              Forecast
            </th>
          </tr>
        </thead>
        <tbody>
          <tr 
            v-for="workflow in workflowRowsPL" 
            :key="workflow.key"
            :data-testid="`workflow-status-table__pl-row-${workflow.key}`"
          >
            <td 
              class="workflow-type-cell"
              :data-testid="`workflow-status-table__pl-cell-${workflow.key}-label`"
            >
              {{ workflow.label }}
            </td>
            <td 
              class="text-center"
              :data-testid="`workflow-status-table__pl-cell-${workflow.key}-proforma`"
            >
              <div class="d-flex align-center justify-center">
                <StatusIndicator
                  :variant="workflow.proformaVariant"
                  :custom-label="workflow.proformaEnabled ? 'Enabled' : 'Disabled'"
                  size="sm"
                  :data-testid="`workflow-status-table__pl-indicator-${workflow.key}-proforma`"
                />
              </div>
            </td>
            <td 
              class="text-center"
              :data-testid="`workflow-status-table__pl-cell-${workflow.key}-forecast`"
            >
              <div class="d-flex align-center justify-center">
                <StatusIndicator
                  :variant="workflow.forecastVariant"
                  :custom-label="workflow.forecastEnabled ? 'Enabled' : 'Disabled'"
                  size="sm"
                  :data-testid="`workflow-status-table__pl-indicator-${workflow.key}-forecast`"
                />
              </div>
            </td>
          </tr>
        </tbody>
      </template>
    </v-simple-table>

    <!-- Balance Sheet Table -->
    <div class="text-subtitle-2 mb-2 pl-1 font-weight-bold grey--text text--darken-2">Balance Sheet</div>
    <v-simple-table dense class="workflow-table" data-testid="workflow-status-table__bs-table">
      <template v-slot:default>
        <thead class="tableHeaderBg">
          <tr data-testid="workflow-status-table__bs-header-row">
            <th class="text-left" data-testid="workflow-status-table__bs-header-workflow-type" style="width: 40%;">
              Workflow Type
            </th>
            <th class="text-left" data-testid="workflow-status-table__bs-header-proforma" style="width: 30%;">
              {{ $nova.labelsName('cycle_page', 'proforma_shortform_label_balancesheet', 'Proforma') }}
            </th>
            <th class="text-left" data-testid="workflow-status-table__bs-header-forecast" style="width: 30%;">
              {{ $nova.labelsName('cycle_page', 'forecast_shortform_label_balancesheet', 'Forecast') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr 
            v-for="workflow in workflowRowsBS" 
            :key="workflow.key"
            :data-testid="`workflow-status-table__bs-row-${workflow.key}`"
          >
            <td 
              class="workflow-type-cell"
              :data-testid="`workflow-status-table__bs-cell-${workflow.key}-label`"
            >
              {{ workflow.label }}
            </td>
            <td 
              class="text-center"
              :data-testid="`workflow-status-table__bs-cell-${workflow.key}-proforma`"
            >
              <div class="d-flex align-center justify-center">
                <StatusIndicator
                  :variant="workflow.proformaVariant"
                  :custom-label="workflow.proformaEnabled ? 'Enabled' : 'Disabled'"
                  size="sm"
                  :data-testid="`workflow-status-table__bs-indicator-${workflow.key}-proforma`"
                />
              </div>
            </td>
            <td 
              class="text-center"
              :data-testid="`workflow-status-table__bs-cell-${workflow.key}-forecast`"
            >
              <div class="d-flex align-center justify-center">
                <StatusIndicator
                  :variant="workflow.forecastVariant"
                  :custom-label="workflow.forecastEnabled ? 'Enabled' : 'Disabled'"
                  size="sm"
                  :data-testid="`workflow-status-table__bs-indicator-${workflow.key}-forecast`"
                />
              </div>
            </td>
          </tr>
        </tbody>
      </template>
    </v-simple-table>
  </div>
</template>

<script>
/**
 * WorkflowStatusTable Component
 * Displays workflow status in a table format with:
 * - Rows: Local Control, Local Management, Regional Control, Regional Management
 * - Columns: Workflow Type, Proforma, Forecast
 * - Status indicators: Green (Enabled) / Red (Disabled)
 */

import StatusIndicator from './StatusIndicator.vue';
import { STATUS_VARIANTS } from '../utils/statusConstants';

// Workflow configuration mapping keys to row definitions
const WORKFLOW_CONFIG = Object.freeze({
  localControl: {
    key: 'local-control',
    label: 'Local Control',
    pl: {
      proformaKey: 'local control proforma',
      forecastKey: 'local control forecast',
    },
    bs: {
      proformaKey: 'local control proforma bs',
      forecastKey: 'local control forecast bs',
    }
  },
  localManagement: {
    key: 'local-management',
    label: 'Local Management',
    pl: {
      proformaKey: 'local management proforma',
      forecastKey: 'local management forecast',
    },
    bs: {
      proformaKey: 'local management proforma bs',
      forecastKey: 'local management forecast bs',
    }
  },
  regionalControl: {
    key: 'regional-control',
    label: 'Regional Control',
    pl: {
      proformaKey: 'regional control proforma',
      forecastKey: 'regional control forecast',
    },
    bs: {
      proformaKey: 'regional control proforma bs',
      forecastKey: 'regional control forecast bs',
    }
  },
  regionalManagement: {
    key: 'regional-management',
    label: 'Regional Management',
    pl: {
      proformaKey: 'regional management proforma',
      forecastKey: 'regional management forecast',
    },
    bs: {
      proformaKey: 'regional management proforma bs',
      forecastKey: 'regional management forecast bs',
    }
  },
});

export default {
  name: 'WorkflowStatusTable',
  components: {
    StatusIndicator,
  },
  props: {
    /** @type {Array} All workflow text labels */
    allworkflowstext: {
      type: Array,
      default: () => [],
    },
    
    /** @type {Array} Disabled workflow text */
    workflowtext: {
      type: Array,
      default: () => [],
    },
    
    /** @type {Array} All workflow keys for matching */
    allworkflowskeys: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      STATUS_VARIANTS,
    };
  },
  computed: {
    /**
     * Normalized disabled workflow keys for comparison
     */
    disabledWorkflowKeys() {
      return (this.workflowtext || []).map(
        (workflow) => workflow && workflow.toLowerCase()
      );
    },
    
    /**
     * Generate Profit & Loss workflow rows
     */
    workflowRowsPL() {
      return Object.values(WORKFLOW_CONFIG).map((config) => {
        const proformaEnabled = this.isWorkflowEnabled(config.pl.proformaKey);
        const forecastEnabled = this.isWorkflowEnabled(config.pl.forecastKey);
        return {
          key: config.key,
          label: config.label,
          proformaEnabled,
          forecastEnabled,
          proformaVariant: proformaEnabled ? STATUS_VARIANTS.APPROVED : STATUS_VARIANTS.DISABLED,
          forecastVariant: forecastEnabled ? STATUS_VARIANTS.APPROVED : STATUS_VARIANTS.DISABLED,
          isEnabled: proformaEnabled && forecastEnabled,
        };
      });
    },

    /**
     * Generate Balance Sheet workflow rows
     */
    workflowRowsBS() {
      return Object.values(WORKFLOW_CONFIG).map((config) => {
        const proformaEnabled = this.isWorkflowEnabled(config.bs.proformaKey);
        const forecastEnabled = this.isWorkflowEnabled(config.bs.forecastKey);
        return {
          key: config.key,
          label: config.label,
          proformaEnabled,
          forecastEnabled,
          proformaVariant: proformaEnabled ? STATUS_VARIANTS.APPROVED : STATUS_VARIANTS.DISABLED,
          forecastVariant: forecastEnabled ? STATUS_VARIANTS.APPROVED : STATUS_VARIANTS.DISABLED,
          isEnabled: proformaEnabled && forecastEnabled,
        };
      });
    },
  },
  methods: {
    /**
     * Check if a workflow is enabled (not in disabled list)
     * @param {String} workflowKey - The workflow key to check
     * @returns {Boolean} True if enabled
     */
    isWorkflowEnabled(workflowKey) {
      if (!workflowKey) return true;
      const normalizedKey = workflowKey.toLowerCase();
      
      // Check if this workflow key exists in allworkflowskeys
      const keyIndex = this.allworkflowskeys.findIndex(
        (key) => key && key.toLowerCase() === normalizedKey
      );
      
      // If the workflow key is not found in all workflows, assume disabled
      if (keyIndex === -1) {
        // Try alternate matching by partial key
        return !this.disabledWorkflowKeys.some(
          (disabled) => disabled && disabled.includes(normalizedKey)
        );
      }
      
      // Check if it's in the disabled list
      return !this.disabledWorkflowKeys.includes(normalizedKey);
    },
  },
};
</script>

<style scoped>

.workflow-table {
  width: 100%;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;
  overflow: hidden;
}

.workflow-table th {
  padding: 12px 16px !important;
}

.workflow-table td {
  padding: 12px 16px !important;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.workflow-type-cell {
  font-weight: 500;
}

/* Status card design as specified */
/* Alternate row background */
.workflow-table tbody tr:nth-child(odd) {
  background-color: #fafafa;
}

.workflow-table tbody tr:nth-child(even) {
  background-color: #ffffff;
}

/* Hover effect */
.workflow-table tbody tr:hover {
  background-color: #f0f0f0 !important;
}

/* SOD section styling */
.sod-section {
  border-top: 1px solid rgba(0, 0, 0, 0.12);
}
</style>
